/**
 * LadyBoss Cart Drawer — cart-drawer.js
 * ─────────────────────────────────────────────────────────────
 * Performance strategy:
 *   - Section re-renders delegate to Symmetry's native CartForm.refresh()
 *     which uses theme.mergeNodes() — a smart diff that only updates
 *     DOM nodes that actually changed. Much faster than our old full-replace.
 *   - All display updates (subtotal, shipping bar, insurance price) run
 *     from a single fetchCart() result — no redundant fetches.
 *   - Qty +/− buttons debounced 300ms so rapid taps = 1 API call.
 *   - Optimistic UI runs synchronously before any API call.
 * ─────────────────────────────────────────────────────────────
 * Built: LadyBoss July 2026
 */

(function () {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────────
  const INSURANCE_PRODUCT_HANDLE   = 'shipping-protection';
  // Legacy gift-row tracking only; NOT a shipping eligibility guarantee.
  const FREE_SHIPPING_SUB_HANDLES  = ['ignite-hormonal-support', 'ignite-hormonal-support-offer', 'vanilla-cake-lean-protein-20g-protein', 'creatine-hydration', 'ladyboss-creatine', 'ultra-glow-collagen', 'ladyboss-natural-fuel'];

  const INSURANCE_TIERS = [
    { min:     0, max:  2499, price:  50, variantId: '50081065763045' },
    { min:  2500, max:  3499, price: 100, variantId: '50081065795813' },
    { min:  3500, max:  4999, price: 150, variantId: '50081065828581' },
    { min:  5000, max:  6499, price: 250, variantId: '50081065861349' },
    { min:  6500, max:  8499, price: 300, variantId: '50081065894117' },
    { min:  8500, max: 10499, price: 400, variantId: '50081065926885' },
    { min: 10500, max: 12499, price: 450, variantId: '50081065959653' },
    { min: 12500, max: 14999, price: 550, variantId: '50081065992421' },
    { min: 15000, max: 17499, price: 650, variantId: '50081066025189' },
    { min: 17500, max: 19999, price: 750, variantId: '50081066057957' },
    { min: 20000, max: 24999, price: 900, variantId: '50081066090725' },
    { min: 25000, max: 29999, price: 1100, variantId: '50081066123493' },
    { min: 30000, max: 39999, price: 1400, variantId: '50081066156261' },
    { min: 40000, max: Infinity, price: 1800, variantId: '50081066189029' },
  ];

  // ─── STATE ───────────────────────────────────────────────────
  let isUpdating  = false;
  let qtyDebounce = {};
  let easyGiftRequestsInFlight = 0;
  let lastCartActivityAt = 0;
  let easyGiftEventsBound = false;
  let insuranceReconcileTimer = null;
  let insuranceReconcileRunning = false;
  let insuranceOptedOut = false;

  // ─── HELPERS ─────────────────────────────────────────────────

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function getInsuranceTier(cartSubtotalCents) {
    return INSURANCE_TIERS.find(t => cartSubtotalCents >= t.min && cartSubtotalCents <= t.max)
      || INSURANCE_TIERS[INSURANCE_TIERS.length - 1];
  }

  /** Announce completed insurance changes without moving keyboard focus. */
  function announceInsuranceStatus(message) {
    const status = document.getElementById('lb-insurance-status');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => { status.textContent = message; }, 30);
  }

  /** Analyze a cart object — returns subtotal ex-insurance, insurance item, and free-shipping flags */
  function analyzeCart(cart) {
    let subtotalExInsurance  = 0;
    let insuranceItem        = null;
    let hasRealItems         = false;
    // Legacy subscription/gift classification; never used to promise shipping.
    let hasFreeShippingSub   = false;

    cart.items.forEach(item => {
      if (item.handle === INSURANCE_PRODUCT_HANDLE) {
        insuranceItem = item;
      } else {
        subtotalExInsurance += item.final_line_price;
        hasRealItems = true;
        if (item.selling_plan_allocation &&
            FREE_SHIPPING_SUB_HANDLES.includes(item.handle)) {
          hasFreeShippingSub = true;
        }
      }
    });

    return { subtotalExInsurance, insuranceItem, hasFreeShippingSub, hasRealItems };
  }

  // ─── SYMMETRY NATIVE REFRESH ─────────────────────────────────

  /**
   * Trigger Symmetry's CartForm.refresh() — this fetches the section HTML
   * and uses theme.mergeNodes() to do a smart diff update of only changed nodes.
   * Much faster than our old full innerHTML replace.
   * After the merge fires the 'on:cart:after-merge' event, we re-apply
   * our display updates so subtotal/bar/insurance price are always accurate.
   */
  function triggerSymmetryRefresh() {
    const cartForm = document.querySelector('cart-form[data-ajax-update]');
    if (cartForm && typeof cartForm.refresh === 'function') {
      cartForm.refresh();
    } else {
      // Fallback: fire the event Symmetry listens to
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    }
  }

  // ─── API WRAPPERS ─────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  }

  function markCartActivity() {
    lastCartActivityAt = Date.now();
  }

  function pause(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  function cartSignature(cart) {
    return (cart.items || []).map(item => [item.key, item.quantity, item.final_line_price]).join('|');
  }

  function bindEasyGiftEvents() {
    if (easyGiftEventsBound || !window.EasyGift || !window.EasyGift.Events ||
        typeof window.EasyGift.Events.subscribe !== 'function') return false;

    window.EasyGift.Events.subscribe('beforeCartRequest', () => {
      easyGiftRequestsInFlight += 1;
      markCartActivity();
    });
    window.EasyGift.Events.subscribe('afterCartRequest', () => {
      easyGiftRequestsInFlight = Math.max(0, easyGiftRequestsInFlight - 1);
      markCartActivity();
      scheduleInsuranceReconciliation();
    });
    easyGiftEventsBound = true;
    return true;
  }

  function scheduleEasyGiftEventBinding(attempt = 0) {
    if (bindEasyGiftEvents() || attempt >= 25) return;
    window.setTimeout(() => scheduleEasyGiftEventBinding(attempt + 1), 200);
  }

  function hasRecentEasyGiftActivity() {
    return easyGiftRequestsInFlight > 0 || (Date.now() - lastCartActivityAt) < 1100;
  }

  /**
   * EG owns product and free-gift mutations. Insurance is deliberately reconciled
   * only after EG has been quiet long enough to finish its serialized request queue.
   */
  function scheduleInsuranceReconciliation(delay = 1250) {
    window.clearTimeout(insuranceReconcileTimer);
    insuranceReconcileTimer = window.setTimeout(reconcileInsuranceAfterEasyGift, delay);
  }

  async function reconcileInsuranceAfterEasyGift() {
    if (insuranceReconcileRunning || isUpdating || hasRecentEasyGiftActivity()) {
      scheduleInsuranceReconciliation(350);
      return;
    }

    insuranceReconcileRunning = true;
    try {
      let cart = await fetchCart();
      if (hasRecentEasyGiftActivity()) {
        scheduleInsuranceReconciliation(350);
        return;
      }

      const { insuranceItem, hasRealItems } = analyzeCart(cart);
      const toggle = document.getElementById('lb-insurance-toggle');

      if (!hasRealItems) {
        insuranceOptedOut = false;
        if (toggle) toggle.checked = false;
        if (insuranceItem) {
          cart = await removeInsuranceFromCart(cart);
          triggerSymmetryRefresh();
        }
        updateAllDisplays(cart);
        return;
      }

      if (!toggle) {
        updateAllDisplays(cart);
        return;
      }

      if (!toggle.checked && !insuranceOptedOut && !insuranceItem) {
        toggle.checked = true;
      }

      if (!toggle.checked) {
        updateAllDisplays(cart);
        return;
      }

      const syncedCart = await syncInsuranceTier(cart);
      updateAllDisplays(syncedCart);
      if (cartSignature(cart) !== cartSignature(syncedCart)) triggerSymmetryRefresh();
    } catch (error) {
      console.error('[LadyBoss Cart] Deferred insurance reconciliation error:', error);
    } finally {
      insuranceReconcileRunning = false;
    }
  }

  /** Add variant — returns updated cart */
  async function addToCart(variantId, quantity = 1, properties = {}) {
    markCartActivity();
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    return fetchCart();
  }

  /** Update line item by key — returns updated cart */
  async function updateCartItem(key, quantity) {
    markCartActivity();
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  }

  /** Remove all insurance line items — returns updated cart */
  async function removeInsuranceFromCart(cart) {
    const insuranceItems = cart.items.filter(i => i.handle === INSURANCE_PRODUCT_HANDLE);
    let updatedCart = cart;
    for (const item of insuranceItems) {
      updatedCart = await updateCartItem(item.key, 0);
    }
    return updatedCart;
  }

  // ─── DISPLAY UPDATES ─────────────────────────────────────────

  /**
   * Update subtotal, insurance price label, and shipping bar from a cart object.
   * Called immediately after any API change for instant visual feedback,
   * and again after Symmetry's merge completes (via on:cart:after-merge).
   */
  function updateAllDisplays(cart) {
    const { subtotalExInsurance, hasFreeShippingSub, hasRealItems } = analyzeCart(cart);

    // Subtotal
    const subtotalEl = document.getElementById('lb-cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Insurance price label
    const displayEl = document.querySelector('[data-insurance-display]');
    if (displayEl) {
      const tier = getInsuranceTier(subtotalExInsurance);
      displayEl.textContent = formatMoney(tier.price);
    }

    // Shipping bar
    updateShippingBarDOM(cart);
  }

  // REWARDS_PURE_START — private; local tests extract this block without globals.
  function getRewardsState(cart, now, productFlags = {}) {
    const active = now < Date.parse('2026-11-09T08:00:00Z');
    const usd = cents => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    const items = cart.items || [];
    let spend = 0;
    let known = true;
    items.forEach(item => {
      const line = Math.max(0, Number(item.final_line_price) || 0);
      if (!line) return;
      let giftCard = item.gift_card;
      if (typeof giftCard !== 'boolean') giftCard = productFlags[String(item.variant_id || item.id)];
      if (typeof giftCard !== 'boolean') known = false;
      if (giftCard !== true) spend += line;
    });
    const titles = {
      'Early BF - $25 off $125+': 2500,
      'Early BF - $40 off $175+': 4000,
      'Early BF - $55 off $225+': 5500
    };
    let applied = 0;
    let orderDiscounts = 0;
    (cart.cart_level_discount_applications || []).forEach(discount => {
      const amount = Math.max(0, Number(discount.total_allocated_amount) || 0);
      orderDiscounts += amount;
      if (Object.prototype.hasOwnProperty.call(titles, discount.title) && titles[discount.title] === amount) applied = Math.max(applied, amount);
    });
    // Native all-items thresholds include paid protection; purchased gift cards
    // are excluded. BF never subtracts its own ORDER saving from qualification.
    const shipping = Math.max(0, spend - orderDiscounts);
    const currency = typeof cart.currency === 'string' ? cart.currency : cart.currency && cart.currency.iso_code;
    const precise = currency === 'USD' && known;
    const tier = spend >= 22500 ? 5500 : spend >= 17500 ? 4000 : spend >= 12500 ? 2500 : 0;
    let goal = spend < 12500 ? 12500 : spend < 17500 ? 17500 : 22500;
    const saving = goal === 12500 ? 2500 : goal === 17500 ? 4000 : 5500;
    let value = spend;
    let name = 'Early Black Friday qualifying spend';
    let label;
    if (!precise) label = 'Shipping offers confirmed at checkout';
    else if (shipping < 9900 || !active) label = shipping >= 9900 ? 'Shipping threshold reached · eligible rates only' : `Add ${usd(9900 - shipping)} for eligible free shipping`;
    else if (tier === 5500) label = '$55 qualifying tier reached';
    else label = `Add ${usd(goal - spend)} more to unlock ${usd(saving)} off total`;
    if (shipping < 9900 || !active) {
      goal = 9900;
      value = shipping;
      name = 'Eligible shipping spend';
    }
    let hint = 'Early BF: $25 / $40 / $55 total off';
    if (!precise) hint = 'Early BF eligibility and savings confirmed at checkout.';
    else if (applied) hint = `${usd(applied)} Early BF applied${shipping >= 9900 ? ' · Shipping threshold reached' : ''}`;
    else if (tier) hint = `${usd(tier)} tier reached · Final discount at checkout`;
    value = Math.min(goal, Math.max(0, value));
    return { active, precise, spend, shipping, tier, applied, goal, value, name, label, hint, percent: value / goal * 100, empty: items.length === 0 };
  }
  // REWARDS_PURE_END

  function setRewardsText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function updateShippingBarDOM(cart) {
    document.querySelectorAll('.lb-cart-rewards').forEach(wrap => {
      let productFlags = {};
      try { productFlags = JSON.parse(wrap.querySelector('[data-rewards-products]').textContent); } catch (_) { /* Conservative fallback. */ }
      const state = getRewardsState(cart, Date.now(), productFlags);
      wrap.hidden = state.empty;
      wrap.dataset.rewardsShipping = state.shipping;
      wrap.dataset.rewardsCurrency = state.precise ? 'USD' : '';
      setRewardsText(wrap.querySelector('[data-rewards-label]'), state.label);
      setRewardsText(wrap.querySelector('.lb-cart-rewards__hint'), state.hint);
      wrap.querySelectorAll('[data-rewards-bf]').forEach(node => { node.hidden = !state.active; });
      const track = wrap.querySelector('[data-rewards-progress]');
      if (track) {
        track.hidden = !state.precise;
        track.setAttribute('aria-label', state.name);
        track.setAttribute('aria-valuemax', state.goal);
        track.setAttribute('aria-valuenow', state.value);
        track.setAttribute('aria-valuetext', state.label);
      }
      const fill = wrap.querySelector('[data-rewards-fill]');
      if (fill) fill.style.width = state.percent + '%';
    });
  }

  // One expiry timeout, not a poller. Recheck on tab resume and native merges.
  function expireRewards() {
    document.querySelectorAll('.lb-cart-rewards').forEach(wrap => {
      if (Date.now() < Date.parse(wrap.dataset.rewardsEnd)) return;
      wrap.querySelectorAll('[data-rewards-bf]').forEach(node => { node.hidden = true; });
      const shipping = Number(wrap.dataset.rewardsShipping) || 0;
      const precise = wrap.dataset.rewardsCurrency === 'USD';
      const label = !precise ? 'Shipping offers confirmed at checkout' : shipping >= 9900 ? 'Shipping threshold reached · eligible rates only' : `Add ${formatMoney(9900 - shipping)} for eligible free shipping`;
      setRewardsText(wrap.querySelector('[data-rewards-label]'), label);
      const track = wrap.querySelector('[data-rewards-progress]');
      if (track) {
        track.hidden = !precise;
        track.setAttribute('aria-label', 'Eligible shipping spend');
        track.setAttribute('aria-valuemax', '9900');
        track.setAttribute('aria-valuenow', Math.min(9900, shipping));
        track.setAttribute('aria-valuetext', label);
      }
      const fill = wrap.querySelector('[data-rewards-fill]');
      if (fill) fill.style.width = Math.min(100, shipping / 9900 * 100) + '%';
    });
  }

  // ─── INSURANCE LOGIC ─────────────────────────────────────────

  async function handleInsuranceToggle(checked) {
    if (isUpdating) return;
    insuranceOptedOut = !checked;
    isUpdating = true;

    const toggle       = document.getElementById('lb-insurance-toggle');
    const insuranceRow = document.querySelector('.lb-insurance');

    if (toggle) {
      toggle.disabled = true;
      toggle.setAttribute('aria-busy', 'true');
    }
    if (insuranceRow) insuranceRow.style.opacity = '0.55';
    announceInsuranceStatus('Updating Shipping Protection.');

    try {
      // Fetch cart once — used for both remove and add.
      let cart = await fetchCart();
      const { subtotalExInsurance } = analyzeCart(cart);
      const tier = getInsuranceTier(subtotalExInsurance);

      // Remove any existing insurance first.
      cart = await removeInsuranceFromCart(cart);

      if (checked && tier.variantId) {
        cart = await addToCart(tier.variantId, 1, { _insurance: 'true' });
      }

      // Instant display update
      updateAllDisplays(cart);
      announceInsuranceStatus(
        checked
          ? `Shipping Protection added. ${formatMoney(tier.price)} has been added to your order.`
          : 'Shipping Protection removed from your order.'
      );

      // Symmetry handles the DOM merge
      triggerSymmetryRefresh();

    } catch (err) {
      console.error('[LadyBoss Cart] Insurance toggle error:', err);
      if (toggle) toggle.checked = !checked;
      announceInsuranceStatus('Shipping Protection could not be updated. Please try again.');
    } finally {
      isUpdating = false;
      if (toggle) {
        toggle.disabled = false;
        toggle.removeAttribute('aria-busy');
      }
      if (insuranceRow) insuranceRow.style.opacity = '';
    }
  }

  /** Sync insurance to the correct tier after cart total changes */
  async function syncInsuranceTier(cart) {
    const toggle = document.getElementById('lb-insurance-toggle');
    if (!toggle || !toggle.checked) return cart;

    const { subtotalExInsurance, insuranceItem, hasRealItems } = analyzeCart(cart);
    const correctTier = getInsuranceTier(subtotalExInsurance);

    if (insuranceItem && String(insuranceItem.variant_id) === String(correctTier.variantId)) {
      return cart; // already correct
    }

    let updated = await removeInsuranceFromCart(cart);
    if (correctTier.variantId) {
      updated = await addToCart(correctTier.variantId, 1, { _insurance: 'true' });
    }
    return updated;
  }

  /**
   * Auto-add insurance on drawer open.
   * Only updates displays — does NOT trigger a section refresh
   * (the drawer was just opened, items are already rendered correctly).
   */
  async function autoAddInsuranceIfNeeded() {
    const toggle = document.getElementById('lb-insurance-toggle');
    if (!toggle || !toggle.checked) return;

    let cart = await fetchCart();
    const { subtotalExInsurance, insuranceItem, hasRealItems, hasFreeShippingSub } = analyzeCart(cart);

    if (!hasRealItems) return;

    if (insuranceItem) {
      // Already in cart — just update displays
      updateAllDisplays(cart);
      return;
    }

    // Add insurance
    const tier = getInsuranceTier(subtotalExInsurance);
    if (tier.variantId) {
      cart = await addToCart(tier.variantId, 1, { _insurance: 'true' });
      updateAllDisplays(cart);
      // Trigger a section refresh so the insurance line item appears in the list
      triggerSymmetryRefresh();
    }
  }

  // ─── QUANTITY UPDATES ─────────────────────────────────────────

  function handleQtyChange(key, newQty) {
    // Optimistic UI
    const itemRow = document.querySelector(`[data-merge-list-item*="item-key:${key}"]`);

    if (newQty <= 0 && itemRow) {
      itemRow.style.transition = 'opacity 0.18s, max-height 0.25s, padding 0.25s, margin 0.25s';
      itemRow.style.opacity    = '0';
      itemRow.style.maxHeight  = '0';
      itemRow.style.overflow   = 'hidden';
      itemRow.style.paddingTop    = '0';
      itemRow.style.paddingBottom = '0';
      itemRow.style.marginBottom  = '0';
      // If this item is a free-shipping-qualifying subscription, hide gifted items
      // ONLY if no other qualifying subscription remains in the cart
      if (itemRow.classList.contains('lb-cart-item') && itemRow.dataset.freeShippingSub === 'true') {
        const otherQualifyingSubs = document.querySelectorAll('.lb-cart-item[data-free-shipping-sub="true"]');
        const remainingCount = Array.from(otherQualifyingSubs).filter(row => row !== itemRow).length;
        if (remainingCount === 0) {
          hideGiftedItemsOptimistically();
        }
      }
    } else if (itemRow) {
      const qtyDisplay = itemRow.querySelector('.lb-qty__val');
      if (qtyDisplay) qtyDisplay.textContent = newQty;
      const minusBtn = itemRow.querySelector('.js-qty-minus');
      const plusBtn  = itemRow.querySelector('.js-qty-plus');
      if (minusBtn) minusBtn.dataset.qty = newQty - 1;
      if (plusBtn)  plusBtn.dataset.qty  = newQty + 1;
    }

    // Debounce API call 300ms
    if (qtyDebounce[key]) clearTimeout(qtyDebounce[key]);
    qtyDebounce[key] = setTimeout(() => {
      delete qtyDebounce[key];
      _executeQtyChange(key, newQty);
    }, 300);
  }

  // Hide free gift rows optimistically when a qualifying subscription item is removed.
  // EG Add to Cart will remove them server-side; this just makes it feel instant.
  function hideGiftedItemsOptimistically() {
    document.querySelectorAll('.lb-cart-item[data-gifted="true"]').forEach(row => {
      row.style.transition = 'opacity 0.2s, max-height 0.3s';
      row.style.opacity = '0';
      row.style.maxHeight = '0';
      row.style.overflow = 'hidden';
      row.style.marginBottom = '0';
      row.style.padding = '0';
    });
  }

  async function _executeQtyChange(key, newQty) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      let cart = await updateCartItem(key, Math.max(0, newQty));
      const { hasRealItems } = analyzeCart(cart);

      if (!hasRealItems) {
        // Cart is empty — clear insurance and toggle in parallel with section refresh
        const toggle = document.getElementById('lb-insurance-toggle');
        if (toggle) toggle.checked = false;
        await Promise.all([
          removeInsuranceFromCart(cart),
          Promise.resolve(triggerSymmetryRefresh())
        ]);
        updateAllDisplays({ items: [], total_price: 0 });
      } else {
        // Run insurance sync and section refresh in parallel
        const [syncedCart] = await Promise.all([
          syncInsuranceTier(cart),
          Promise.resolve(triggerSymmetryRefresh())
        ]);
        updateAllDisplays(syncedCart || cart);
      }

    } catch (err) {
      console.error('[LadyBoss Cart] Qty update error:', err);
    } finally {
      isUpdating = false;
    }
  }

  // ─── EMPTY CART ───────────────────────────────────────────────

  async function handleEmptyCart() {
    if (isUpdating) return;
    isUpdating = true;
    try {
      // Optimistic: fade out all item rows immediately
      document.querySelectorAll('.lb-cart-item').forEach(row => {
        row.style.transition = 'opacity 0.2s';
        row.style.opacity = '0';
      });
      // Clear entire cart in one call
      await fetch('/cart/clear.js', { method: 'POST' });
      // The next real cart item should return to the required default-on state.
      insuranceOptedOut = false;
      const toggle = document.getElementById('lb-insurance-toggle');
      if (toggle) toggle.checked = false;
      // Update displays immediately
      updateAllDisplays({ items: [], total_price: 0 });
      // Let Symmetry re-render the empty state
      triggerSymmetryRefresh();
    } catch (err) {
      console.error('[LadyBoss Cart] Empty cart error:', err);
    } finally {
      isUpdating = false;
    }
  }

  // ─── UPSELL ADD TO CART ───────────────────────────────────────

  async function handleUpsellAdd(variantId, btn) {
    if (isUpdating) return;
    isUpdating = true;

    const originalText = btn.textContent;
    btn.classList.add('is-adding');
    btn.textContent = '✓';
    btn.disabled = true;

    // Hide the pill immediately
    const pill = btn.closest('.lb-upsell-pill');
    if (pill) {
      pill.style.transition = 'opacity 0.2s, max-height 0.25s';
      pill.style.opacity    = '0';
      pill.style.maxHeight  = '0';
      pill.style.overflow   = 'hidden';
      pill.style.marginBottom = '0';
    }

    try {
      let cart = await addToCart(variantId, 1);
      cart = await syncInsuranceTier(cart);
      updateAllDisplays(cart);
      triggerSymmetryRefresh();
    } catch (err) {
      console.error('[LadyBoss Cart] Upsell add error:', err);
      btn.classList.remove('is-adding', 'is-added');
      btn.textContent = originalText;
      btn.disabled = false;
      if (pill) {
        pill.style.opacity = '';
        pill.style.maxHeight = '';
      }
    } finally {
      isUpdating = false;
    }
  }

  // ─── EVENT DELEGATION ────────────────────────────────────────

  let delegationBound = false;

  function initCartListeners() {
    const drawer = document.querySelector('.lb-cart-drawer');
    if (!drawer || delegationBound) return;
    delegationBound = true;

    drawer.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('.js-qty-minus');
      if (minusBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(minusBtn.dataset.key, parseInt(minusBtn.dataset.qty, 10));
        return;
      }
      const plusBtn = e.target.closest('.js-qty-plus');
      if (plusBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(plusBtn.dataset.key, parseInt(plusBtn.dataset.qty, 10));
        return;
      }
      const removeBtn = e.target.closest('.js-remove-item');
      if (removeBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(removeBtn.dataset.key, 0);
        return;
      }
      const upsellBtn = e.target.closest('.js-upsell-add');
      if (upsellBtn) {
        e.preventDefault(); e.stopPropagation();
        handleUpsellAdd(upsellBtn.dataset.variantId, upsellBtn);
        return;
      }
      const emptyBtn = e.target.closest('.js-empty-cart');
      if (emptyBtn) {
        e.preventDefault(); e.stopPropagation();
        handleEmptyCart();
        return;
      }
    });

    drawer.addEventListener('change', (e) => {
      if (e.target.id === 'lb-insurance-toggle') {
        handleInsuranceToggle(e.target.checked);
      }
    });

    // EG now owns checkout reconciliation through its native setting. Do not add
    // a second checkout poller here: extra cart reads were part of the conflict.

    // A merge may follow an EG mutation. Schedule one deferred reconciliation,
    // never an immediate cart fetch that competes with EG's serialized queue.
    drawer.addEventListener('on:cart:after-merge', () => {
      scheduleInsuranceReconciliation();
    });
  }

  // ─── INIT ─────────────────────────────────────────────────────

  function init() {
    expireRewards();
    const untilEnd = Date.parse('2026-11-09T08:00:00Z') - Date.now();
    if (untilEnd > 0) window.setTimeout(expireRewards, Math.min(untilEnd, 2147483647));
    document.addEventListener('visibilitychange', expireRewards);
    document.addEventListener('on:cart:after-merge', expireRewards);
    // Cart page relies on Liquid/native merges: no extra reads or mutations.
    if (!document.querySelector('.lb-cart-drawer')) return;
    initCartListeners();
    scheduleEasyGiftEventBinding();

    // Initial display update on page load
    fetchCart().then(cart => updateAllDisplays(cart));

    /**
     * Product forms and EG both emit cart activity. This handler deliberately makes
     * no cart request. It queues one insurance reconciliation after EG is idle.
     */
    document.addEventListener('on:cart:add', () => {
      markCartActivity();
      scheduleInsuranceReconciliation();
    });

    /**
     * Let a just-finished EG flow settle before reading cart state. When the drawer
     * opens independently, one normal display read is safe and keeps values current.
     */
    document.addEventListener('dispatch:cart-drawer:open', () => {
      if (hasRecentEasyGiftActivity()) {
        scheduleInsuranceReconciliation();
        return;
      }
      fetchCart().then(updateAllDisplays).catch(error => {
        console.error('[LadyBoss Cart] drawer open display error:', error);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
